// Enterprise Pulse V1 (PURE, dependency-free, versioned) — mirrors the Health-V1
// honesty pattern EXACTLY. It composes ONLY real signals into a readout of business
// ACTIVITY / DIRECTION / MOMENTUM. It is NOT "enterprise value" and NOT a fabricated
// universal index. Each facet declares its derivation and exposes its inputs; a facet
// whose signal is disabled/absent is EXCLUDED ("not available"), never imputed.
//
// Real sources:
//   activity  : GET /api/intelligence/observations (count/rate), GET /api/outcomes/summary
//               (+ optional GET /api/velocity/metrics), infra: /bu/health, /schedulers/health,
//               /integrations/health
//   direction : GET /api/outcomes/summary by_result (current WIN-LOSS balance; NOT a trend)
//   momentum  : GET /api/velocity/trends (a REAL time-series). Absent unless >=2 numeric
//               periods exist — a momentum/trend is NEVER fabricated from a single point.

function num(x) {
  return typeof x === "number" && !Number.isNaN(x) ? x : null;
}

function infraReadiness({ buHealth, schedulers, integrations }) {
  const parts = [];
  if (buHealth && Array.isArray(buHealth.business_units) && buHealth.business_units.length) {
    const green = buHealth.business_units.filter((b) => b.status === "green").length;
    parts.push({ key: "bu", green, total: buHealth.business_units.length, ok: green === buHealth.business_units.length });
  }
  if (schedulers && typeof schedulers === "object") {
    const ok = schedulers.status ? ["ok", "green", "healthy"].includes(schedulers.status) : true;
    parts.push({ key: "schedulers", ok });
  }
  if (integrations && typeof integrations === "object") {
    // tolerate a few shapes: {status}, {healthy:bool}, {integrations:[{status}]}
    let ok = true;
    if (integrations.status) ok = ["ok", "green", "healthy"].includes(integrations.status);
    else if (typeof integrations.healthy === "boolean") ok = integrations.healthy;
    else if (Array.isArray(integrations.integrations)) ok = integrations.integrations.every((i) => i.status !== "red" && i.connected !== false);
    parts.push({ key: "integrations", ok });
  }
  if (!parts.length) return null;
  return parts;
}

export function deriveEnterprisePulseV1({ buHealth, schedulers, integrations, observations, outcomesSummary, velocityMetrics, velocityTrends } = {}) {
  const facets = [];

  // ── ACTIVITY — current live-signal volume + operational readiness (real counts). ──
  {
    const obsCount = Array.isArray(observations)
      ? observations.length
      : observations && num(observations.total) != null
      ? observations.total
      : null;
    const outcomesTracked = outcomesSummary && num(outcomesSummary.total) != null ? outcomesSummary.total : null;
    const readiness = infraReadiness({ buHealth, schedulers, integrations });
    const tcf = velocityMetrics && velocityMetrics.test_cycle_frequency;
    const available = obsCount != null || outcomesTracked != null || readiness != null;
    facets.push({
      key: "activity",
      label: "Activity",
      available,
      inputs: available ? { observations: obsCount, outcomes_tracked: outcomesTracked, readiness, test_cycle_frequency: tcf != null ? tcf : undefined } : null,
      note: available ? "current live-signal volume + operational readiness" : "no activity signals available",
      source: "intelligence/observations + outcomes/summary + bu/scheduler/integrations health",
    });
  }

  // ── DIRECTION — current WIN-LOSS balance (explicitly NOT a trend). ──
  {
    const r = outcomesSummary && outcomesSummary.by_result;
    if (r) {
      const win = r.WIN || r.SUCCESS || 0;
      const loss = r.LOSS || r.FAILURE || 0;
      if (win + loss > 0) {
        const net = win - loss;
        facets.push({
          key: "direction",
          label: "Direction",
          available: true,
          value: net > 0 ? "positive" : net < 0 ? "negative" : "even",
          inputs: { win, loss, net },
          note: "current outcome balance (win − loss); NOT a time-derivative",
          source: "GET /api/outcomes/summary -> by_result",
        });
      } else {
        facets.push({ key: "direction", label: "Direction", available: false, note: "no resolved outcomes", source: "GET /api/outcomes/summary" });
      }
    } else {
      facets.push({ key: "direction", label: "Direction", available: false, note: "not counted", source: "GET /api/outcomes/summary" });
    }
  }

  // ── MOMENTUM — REAL trend from velocity/trends; absent unless >=2 numeric periods. ──
  {
    const trends = velocityTrends && Array.isArray(velocityTrends.trends) ? velocityTrends.trends : Array.isArray(velocityTrends) ? velocityTrends : [];
    const series = trends
      .map((p) => (p && typeof p === "object" ? num(p.value != null ? p.value : p.rate != null ? p.rate : p.count) : num(p)))
      .filter((v) => v != null);
    if (series.length >= 2) {
      const from = series[0];
      const to = series[series.length - 1];
      facets.push({
        key: "momentum",
        label: "Momentum",
        available: true,
        value: to > from ? "accelerating" : to < from ? "slowing" : "steady",
        inputs: { periods: series.length, from, to, delta: +(to - from).toFixed(4) },
        note: "change across real velocity-trend periods",
        source: "GET /api/velocity/trends",
      });
    } else {
      // No time-series -> momentum is honestly ABSENT (never imputed from one point).
      facets.push({
        key: "momentum",
        label: "Momentum",
        available: false,
        note: "no time-series (velocity/trends has <2 periods) — not imputed",
        source: "GET /api/velocity/trends",
      });
    }
  }

  const counted = facets.filter((f) => f.available);
  return {
    version: "V1",
    definition: "readout of ACTIVITY / DIRECTION / MOMENTUM from real signals only; unavailable facets excluded (not imputed); not an enterprise-value index",
    facets,
    availableKeys: counted.map((f) => f.key),
    unavailableKeys: facets.filter((f) => !f.available).map((f) => f.key),
    hasAny: counted.length > 0,
  };
}
